import React, { useEffect } from 'react';
import { Refine } from '@pankod/refine-core';
import { ConfigProvider } from 'antd';
import {
  notificationProvider,
  ReadyPage,
  ErrorComponent,
} from '@pankod/refine-antd';
import 'styles/antd.less';
import routerProvider from '@pankod/refine-react-router-v6';
import { authProvider } from './services/authProvider';
import {
  Title,
  Header,
  Sider,
  Footer,
  Layout,
  OffLayoutArea,
} from './components/layout';
import { useTranslation } from 'react-i18next';
import ApiProvider from './services/ApiClass';
import accessControlProvider from './services/accessControlProvider';
import { UserList, UserEdit, UserShow, UserCreate } from './pages/user/user';
import { OwnershipList, OwnershipEdit, OwnershipShow, OwnershipCreate } from './pages/ownership';
import { ConveyanceList, ConveyanceCreate, ConveyanceEdit } from './pages/conveyance';

// Placeholder pour les pages de "Contrats" (à créer si nécessaire)
import { Profile } from './pages/user';
import { Faq } from './pages/faq';
import { Login } from './pages/admin/login';
import { Dashboard } from './pages/dashboard';
import  Home  from './pages/home';
import packageJson from '../package.json';
import UserHandler from './util/UserHandler';

window.addEventListener('error', (e) => {
  if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
    const resizeObserverErrDiv = document.getElementById('webpack-dev-server-client-overlay-div');
    const resizeObserverErr = document.getElementById('webpack-dev-server-client-overlay');
    if (resizeObserverErr) {
      resizeObserverErr.setAttribute('style', 'display: none');
    }
    if (resizeObserverErrDiv) {
      resizeObserverErrDiv.setAttribute('style', 'display: none');
    }
  }
});

function App() {
  const { t, i18n } = useTranslation();
  const authUser = UserHandler.getInstance().user;

  const i18nProvider = {
    translate: (key: string, params: object) => t(key, params),
    changeLocale: (lang: string) => i18n.changeLanguage(lang),
    getLocale: () => i18n.language,
  };

  const { RouterComponent } = routerProvider;
  const CustomRouterComponent = () => (
    <RouterComponent basename={packageJson.homepage} />
  );

  useEffect(() => {
    const lang = navigator.language.split('-')[0].toLowerCase();
    i18n.changeLanguage(lang);
  }, []);

  return (
    <ConfigProvider>
      <Refine
        notificationProvider={notificationProvider}
        ReadyPage={ReadyPage}
        catchAll={<ErrorComponent />}
        routerProvider={{
          ...routerProvider,
          RouterComponent: CustomRouterComponent,
          routes: [
            {
              path: '/user/profile',
              element: <Profile />,
              layout: true,
            },
            {
              path: '/faq',
              element: <Faq />,
              layout: true,
            },
          ],
        }}
        dataProvider={ApiProvider(`${process.env.REACT_APP_API}`)}
        authProvider={authProvider}
        accessControlProvider={accessControlProvider}
        LoginPage={Login}
        DashboardPage={Home || Dashboard}
        resources={[
          {
            name: 'user',
            list: UserList,
            create: UserCreate,
            edit: UserEdit,
            show: UserShow,
            options: { label: t('menu.user') },
          },
          {
            name: 'ownership',
            list: OwnershipList,
            create: OwnershipCreate,
            edit: OwnershipEdit,
            show: OwnershipShow,
            options: { label: t('menu.ownership') },
          },
          {
            name: 'conveyance',
            list: ConveyanceList, 
            create: ConveyanceCreate,
            edit: ConveyanceEdit,
            options: { label: t('menu.conveyances', { defaultValue: 'Contrats' }) },
          },
        ]}
        Title={Title}
        Header={Header}
        Sider={Sider}
        Footer={Footer}
        Layout={Layout}
        OffLayoutArea={OffLayoutArea}
        i18nProvider={i18nProvider}
      />
    </ConfigProvider>
  );
}

export default App;